/**
 * AuthContext wires the module-level `authStore` into the React tree so
 * components re-render when sign-in / sign-out happens. The store is still the
 * source of truth — this context just publishes ticks and exposes the derived
 * operator identity.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getAccessToken,
  getClaims,
  registerSignOutCallback,
  setAccessToken,
  signOut as storeSignOut,
} from './authStore';
import {
  AuthContext,
  deriveOperator,
  type AuthContextValue,
  type AuthStatus,
  type CurrentOperator,
} from './authContextValue';

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus = 'booting' }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [operator, setOperator] = useState<CurrentOperator | null>(() =>
    deriveOperator(getClaims()),
  );
  const [sessionExpired, setSessionExpired] = useState(false);

  const refreshFromStore = useCallback(() => {
    const token = getAccessToken();
    setStatus(token ? 'authed' : 'unauthed');
    setOperator(deriveOperator(getClaims()));
    // A token means we're (re-)authenticated — clear any stale expiry flag so the
    // interstitial can't linger after a real login round-trip / fresh authed mount.
    if (token) setSessionExpired(false);
  }, []);

  useEffect(() => {
    registerSignOutCallback((expired: boolean) => {
      setStatus('unauthed');
      setOperator(null);
      // Only a runtime expiry (signOut({ expired: true })) raises the interstitial;
      // explicit logout (bare signOut()) leaves it false.
      if (expired) setSessionExpired(true);
    });
    return () => registerSignOutCallback(null);
  }, []);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const setAuthed = useCallback(
    (token: string, expiresAt: string | null) => {
      setAccessToken(token, expiresAt);
      refreshFromStore();
    },
    [refreshFromStore],
  );

  const setUnauthed = useCallback(() => {
    setAccessToken(null);
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const handleSignOut = useCallback(() => {
    storeSignOut();
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      operator,
      sessionExpired,
      clearSessionExpired,
      setAuthed,
      setUnauthed,
      signOut: handleSignOut,
    }),
    [status, operator, sessionExpired, clearSessionExpired, setAuthed, setUnauthed, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
